import { Box, Button } from '@chakra-ui/react';
import { useCallback, useEffect, useState } from 'react';
import { useQuery } from 'urql';
import { useTranslation } from 'react-i18next';
import { faArrowDown } from '@fortawesome/free-solid-svg-icons';
import { isEqual } from 'lodash';
import { ProjectHistoryTimeline } from './ProjectHistoryTimeline';
import { generateDiff } from './utils/diffing';
import { useProjectHistory } from './hooks/useProjectHistory';
import { Drawer } from 'src/components/common/Drawer';
import {
  useProjectEventGroupHistory,
  useResolveEvent,
} from 'src/hooks/event-history';
import { useDrawer } from 'src/components/common/DrawerContext';
import { GetProjectDeepDocument, ModelsDeepFragment } from 'src/gql/graphql';
import SparkelIcon from 'src/components/common/icon/SparkelIcon';
import { useActiveModels } from 'src/hooks/active-models';
import { ModelsMap } from 'src/components/common/ForgeViewer';

type ProjectHistoryDrawerProps = {
  projectId: string;
};

export const ProjectHistoryDrawer = ({
  projectId,
}: ProjectHistoryDrawerProps) => {
  const {
    selectedRange,
    tempModelOverlaysRef,
    setSelectedRange,
    setIsHistoryDrawerOpen,
    setDiff,
    setReferenceState,
    setEventsActiveModels,
    setResolvedStates,
  } = useProjectHistory();
  const {
    eventGroups,
    hasNextPage,
    reloadEventGroups,
    loadMoreGroups,
    isFetchingGroups,
  } = useProjectEventGroupHistory(projectId);
  const {
    getResolvedStateOfEvent,
    unloadOverlayModels,
    unloadModels,
    resolveCurrentProjectLazily,
    clearColors,
  } = useResolveEvent(projectId);
  const { isDrawerOpen } = useDrawer();
  const [eventsBusy, setEventsBusy] = useState(false);
  const [debouncedBusy, setDebouncedBusy] = useState(false);
  const [modelsToUnload, setModelsToUnload] = useState<
    Array<Autodesk.Viewing.Model>
  >([]);

  const { activeModels } = useActiveModels(projectId);

  const { t } = useTranslation('project-history');

  // Debounce the disabled state to avoid brief flickers
  useEffect(() => {
    if (eventsBusy) {
      const id = setTimeout(() => setDebouncedBusy(true), 150);
      return () => clearTimeout(id);
    }
    setDebouncedBusy(false);
  }, [eventsBusy]);

  useEffect(() => {
    setIsHistoryDrawerOpen(isDrawerOpen);
  }, [isDrawerOpen]);

  const [{ data: projectData }] = useQuery({
    query: GetProjectDeepDocument,
    variables: {
      id: projectId,
    },
  });

  const resolveEventsForRange = useCallback(
    async (range: [number, number]) => {
      try {
        clearColors();
        // Ensure that groups are reloaded to ensure that we have the freshest groups
        const syncEventGroups = await reloadEventGroups();
        // Ensure we have atleast one event that we can resolve against our current status
        if (syncEventGroups.length <= 0) return;
        setEventsBusy(true);
        // unload uncommon models from the previous events
        const [baseIdx, secondaryIdx] = range;
        const resolvePromises = [];
        if (baseIdx > 0) {
          const baseGroup = syncEventGroups[baseIdx - 1];
          resolvePromises.push(getResolvedStateOfEvent(baseGroup.firstEvent!));
        } else if (baseIdx <= 0 && projectData?.project) {
          resolvePromises.push(
            resolveCurrentProjectLazily(projectData.project)
          );
        }
        const secondaryEventGroup = syncEventGroups[secondaryIdx - 1];
        resolvePromises.push(
          getResolvedStateOfEvent(secondaryEventGroup.firstEvent!)
        );
        const resolvedStates = await Promise.all(resolvePromises.reverse());
        console.log(resolvedStates);
        const eventsActiveModels: ModelsDeepFragment[] = [];
        const seenSet = new Set();
        const eventsLoadedModelUrns = new Set();
        resolvedStates.forEach((stateItem) => {
          stateItem.propertyContext.loadedModels.forEach((urn: string) =>
            eventsLoadedModelUrns.add(urn)
          );
          if (stateItem.activeModels?.length) {
            stateItem.activeModels.forEach(
              (activeModel: ModelsDeepFragment) => {
                const seen = seenSet.has(activeModel.id);
                if (!seen) {
                  eventsActiveModels.push(activeModel);
                  seenSet.add(activeModel.id);
                }
              }
            );
          }
        });
        const modelsToUnload = [];
        const newTempOverlays: ModelsMap = {};
        for (const tempModelUrn of Object.keys(tempModelOverlaysRef.current)) {
          if (!eventsLoadedModelUrns.has(tempModelUrn)) {
            modelsToUnload.push(tempModelOverlaysRef.current[tempModelUrn]);
          } else {
            newTempOverlays[tempModelUrn] =
              tempModelOverlaysRef.current[tempModelUrn];
          }
        }
        tempModelOverlaysRef.current = newTempOverlays;
        if (modelsToUnload.length) {
          setModelsToUnload(modelsToUnload);
        }
        setEventsActiveModels((prevModels) => {
          if (isEqual(prevModels, eventsActiveModels)) return prevModels;
          return eventsActiveModels;
        });
        const diff = generateDiff(resolvedStates as [any, any]);
        setDiff(diff);
        setReferenceState(resolvedStates[0]);
        setResolvedStates(resolvedStates as [any, any]);
      } catch (err) {
        console.error('Failed to resolve events for range', range, err);
      } finally {
        setEventsBusy(false);
      }
    },
    [
      activeModels,
      projectData?.project,
      getResolvedStateOfEvent,
      resolveCurrentProjectLazily,
      reloadEventGroups,
    ]
  );

  const rangeChangeHandler = useCallback(async (range: [number, number]) => {
    setSelectedRange(range);
  }, []);

  // this handles unloading models when navigating between events
  useEffect(() => {
    if (modelsToUnload.length > 0) {
      unloadModels(modelsToUnload);
      setModelsToUnload([]);
    }
  }, [modelsToUnload]);

  // Listen to the state of the drawer and unload the models that are are currently loaded
  useEffect(() => {
    // If model is closed, unload any overlays added by the event history components
    if (!isDrawerOpen) {
      // This handles unloading models when the modal closes
      unloadOverlayModels();
      clearColors();
      // Reset the state when drawer is closed
      setSelectedRange([0, 1]);
      setDiff(null);
      setReferenceState(null);
      setEventsActiveModels(null);
      setResolvedStates(null);
    }
  }, [isDrawerOpen, unloadOverlayModels]);

  // Separate effect to handle range resolution when drawer is open
  useEffect(() => {
    if (isDrawerOpen) {
      resolveEventsForRange(selectedRange);
    }
  }, [isDrawerOpen, selectedRange, resolveEventsForRange]);

  return (
    <Drawer title={t('project-history:timeline.title')}>
      <Box position="relative">
        <ProjectHistoryTimeline
          key={isDrawerOpen ? 'open' : 'closed'}
          isDisabled={debouncedBusy}
          isLoading={isFetchingGroups && eventGroups.length === 0}
          eventGroups={eventGroups}
          initialSelectedRange={[0, 1]}
          onSelectionChange={rangeChangeHandler}
          projectId={projectId}
          setIsLoading={setEventsBusy}
          reloadEventGroups={reloadEventGroups}
        />
        {hasNextPage && (
          <Button
            isDisabled={eventsBusy}
            onClick={loadMoreGroups}
            size="sm"
            variant="ghost"
            leftIcon={<SparkelIcon icon={faArrowDown} color={'inherit'} />}
          >
            {t('load-more-events')}
          </Button>
        )}
      </Box>
    </Drawer>
  );
};
